
import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card, CardContent } from "@/components/ui/card";
import { useContractTypes } from "@/hooks/useContractTypes";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { IncomingIcon, OutgoingIcon, EmploymentIcon, CircleDotIcon } from "./ContractDirectionIcons";
import { ContractDirection } from "@/lib/types";

// Define the form schema
const formSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  description: z.string().optional(),
  direction: z.enum(["incoming", "outgoing", "employment", "other"]).default("other"),
});

type ContractTypeFormValues = z.infer<typeof formSchema>;

interface ContractTypeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (contractType: { id: string; name: string; description?: string; direction?: ContractDirection }) => void;
}

export function ContractTypeModal({ 
  open, 
  onOpenChange, 
  onSuccess 
}: ContractTypeModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { useCreateContractTypeMutation } = useContractTypes();
  const createContractType = useCreateContractTypeMutation();

  // Initialize form
  const form = useForm<ContractTypeFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: "",
      direction: "other",
    },
  });

  const onSubmit = async (data: ContractTypeFormValues) => {
    try {
      setIsSubmitting(true);
      const result = await createContractType.mutateAsync({
        name: data.name,
        description: data.description,
        direction: data.direction as ContractDirection,
        auto_renews: false, // Set default value for required field
      });
      
      setIsSubmitting(false);
      form.reset();
      onOpenChange(false);
      
      // Call the success callback with the new contract type
      if (onSuccess && result) {
        onSuccess({
          id: result.id,
          name: result.name,
          description: result.description,
          direction: result.direction as ContractDirection
        });
      }
    } catch (error) {
      setIsSubmitting(false);
      console.error("Error submitting form:", error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add New Contract Type</DialogTitle>
          <DialogDescription>
            Create a new contract type that will be available for all contracts.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Contract type name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Contract type description (optional)"
                      {...field}
                      value={field.value || ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="direction"
              render={({ field }) => (
                <FormItem className="space-y-3">
                  <FormLabel>Contract Direction/Purpose</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      value={field.value}
                      className="flex flex-col space-y-1"
                    >
                      <Card className="border rounded-md p-0 overflow-hidden">
                        <CardContent className="p-0">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                            <FormItem className={`flex items-center space-x-3 space-y-0 p-4 cursor-pointer hover:bg-muted/50 ${field.value === "incoming" ? "bg-muted" : ""}`}>
                              <label className="flex flex-1 items-center cursor-pointer">
                                <RadioGroupItem value="incoming" className="peer sr-only" />
                                <div className="flex items-center gap-3 peer-data-[state=checked]:font-medium">
                                  <IncomingIcon className="h-5 w-5 text-emerald-500" />
                                  <div className="flex-1">
                                    <div className="font-medium">Incoming</div>
                                    <div className="text-sm text-muted-foreground">Contracts where you are receiving</div>
                                  </div>
                                  {field.value === "incoming" && <Check className="h-4 w-4 text-primary" />}
                                </div>
                              </label>
                            </FormItem>
                            
                            <FormItem className={`flex items-center space-x-3 space-y-0 p-4 cursor-pointer hover:bg-muted/50 ${field.value === "outgoing" ? "bg-muted" : ""}`}>
                              <label className="flex flex-1 items-center cursor-pointer">
                                <RadioGroupItem value="outgoing" className="peer sr-only" />
                                <div className="flex items-center gap-3 peer-data-[state=checked]:font-medium">
                                  <OutgoingIcon className="h-5 w-5 text-orange-500" />
                                  <div className="flex-1">
                                    <div className="font-medium">Outgoing</div>
                                    <div className="text-sm text-muted-foreground">Contracts where you are paying</div>
                                  </div>
                                  {field.value === "outgoing" && <Check className="h-4 w-4 text-primary" />}
                                </div>
                              </label>
                            </FormItem>
                            
                            <FormItem className={`flex items-center space-x-3 space-y-0 p-4 cursor-pointer hover:bg-muted/50 ${field.value === "employment" ? "bg-muted" : ""}`}>
                              <label className="flex flex-1 items-center cursor-pointer">
                                <RadioGroupItem value="employment" className="peer sr-only" />
                                <div className="flex items-center gap-3 peer-data-[state=checked]:font-medium">
                                  <EmploymentIcon className="h-5 w-5 text-purple-500" />
                                  <div className="flex-1">
                                    <div className="font-medium">Employment</div>
                                    <div className="text-sm text-muted-foreground">Employment/salary related</div>
                                  </div>
                                  {field.value === "employment" && <Check className="h-4 w-4 text-primary" />}
                                </div>
                              </label>
                            </FormItem>
                            
                            <FormItem className={`flex items-center space-x-3 space-y-0 p-4 cursor-pointer hover:bg-muted/50 ${field.value === "other" ? "bg-muted" : ""}`}>
                              <label className="flex flex-1 items-center cursor-pointer">
                                <RadioGroupItem value="other" className="peer sr-only" />
                                <div className="flex items-center gap-3 peer-data-[state=checked]:font-medium">
                                  <CircleDotIcon className="h-5 w-5 text-blue-500" />
                                  <div className="flex-1">
                                    <div className="font-medium">Other</div>
                                    <div className="text-sm text-muted-foreground">Other contract type</div>
                                  </div>
                                  {field.value === "other" && <Check className="h-4 w-4 text-primary" />}
                                </div>
                              </label>
                            </FormItem>
                          </div>
                        </CardContent>
                      </Card>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Creating..." : "Create Contract Type"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
